import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { IoLanguageSharp } from "react-icons/io5";

const BTN_SIZE = 64;
const MARGIN = 16;
const DRAG_THRESHOLD = 4;

export default function LanguageFloating() {
    const { i18n } = useTranslation();
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState({ x: 0, y: 0 });

    const moved = useRef(false);
    const dragging = useRef(false);
    const start = useRef({ x: 0, y: 0 });
    const rafId = useRef(null);
    const nextPos = useRef(null);

    useEffect(() => {
        const x = Math.max(MARGIN, window.innerWidth - BTN_SIZE - MARGIN);
        const y = Math.max(MARGIN, Math.round(window.innerHeight / 2 - BTN_SIZE / 2));
        setPos({ x, y });
    }, []);

    useEffect(() => {
        const root = document.documentElement;
        root.style.setProperty("--lf-x", `${pos.x}px`);
        root.style.setProperty("--lf-y", `${pos.y}px`);
    }, [pos]);

    useEffect(() => {
        const clamp = (v, min, max) => Math.min(Math.max(v, min), max);
        const onResize = () => {
            setPos((p) => ({
                x: clamp(p.x, MARGIN, window.innerWidth - BTN_SIZE - MARGIN),
                y: clamp(p.y, MARGIN, window.innerHeight - BTN_SIZE - MARGIN),
            }));
        };
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    const clamp = (v, min, max) => Math.min(Math.max(v, min), max);

    useEffect(() => {
        const onPointerMove = (e) => {
            if (!dragging.current) return;
            const pt = e.touches ? e.touches[0] : e;

            const nx = clamp(pt.clientX - start.current.x, MARGIN, window.innerWidth - BTN_SIZE - MARGIN);
            const ny = clamp(pt.clientY - start.current.y, MARGIN, window.innerHeight - BTN_SIZE - MARGIN);

            if (Math.abs(nx - pos.x) > DRAG_THRESHOLD || Math.abs(ny - pos.y) > DRAG_THRESHOLD) {
                moved.current = true;

                if (open) setOpen(false);

                nextPos.current = { x: nx, y: ny };
                if (!rafId.current) {
                    rafId.current = requestAnimationFrame(() => {
                        rafId.current = null;
                        if (nextPos.current) setPos(nextPos.current);
                    });
                }
            }
        };

        const onPointerUp = () => {
            if (!dragging.current) return;
            dragging.current = false;
        };

        window.addEventListener("mousemove", onPointerMove);
        window.addEventListener("mouseup", onPointerUp);
        window.addEventListener("touchmove", onPointerMove, { passive: false });
        window.addEventListener("touchend", onPointerUp);
        return () => {
            window.removeEventListener("mousemove", onPointerMove);
            window.removeEventListener("mouseup", onPointerUp);
            window.removeEventListener("touchmove", onPointerMove);
            window.removeEventListener("touchend", onPointerUp);
        };
    }, [pos.x, pos.y]);

    const onPointerDown = (e) => {
        const pt = e.touches ? e.touches[0] : e;
        dragging.current = true;
        moved.current = false;
        start.current = { x: pt.clientX - pos.x, y: pt.clientY - pos.y };
        if (e.cancelable) e.preventDefault();
    };

    const onClickBtn = () => {
        if (moved.current) return;
        setOpen((v) => !v);
    };

    const side = pos.x > window.innerWidth / 2 ? "left" : "right";

    return (
        <>
            <button
                type="button"
                aria-label="Dili değiştir"
                className="lf-btn"
                onMouseDown={onPointerDown}
                onTouchStart={onPointerDown}
                onClick={onClickBtn}
            >
                <span className="lf-flag">
                    <span
                        className={`fi fi-${window.ayarlar.LANGS.find(ls => i18n.resolvedLanguage === ls.lng).flag}`}
                        style={{ width: 24, height: 28 }}
                    />
                </span>
            </button>

            <div className={`lf-menu ${open ? "is-open" : ""} open-${side}`}>
                {window.ayarlar.LANGS.map(({ lng, label, flag }) => {
                    const isActive =
                        (i18n.resolvedLanguage || i18n.language || "").startsWith(lng);

                    return (
                        <button
                            key={lng}
                            className={`lf-item ${isActive ? "is-active" : ""}`}
                            onClick={() => { i18n.changeLanguage(lng); setOpen(false); }}
                            role="menuitemradio"
                            aria-checked={isActive}
                            aria-label={label}
                        >
                            <span className="lf-flag"><span className={`fi fi-${flag}`} /></span>
                            <span className="lf-label">{label}</span>
                        </button>
                    );
                })}
            </div>
        </>
    );
}
